import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useParams, useSearchParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const ANSWERED = {
  approved: { color: 'green', text: 'You approved this. We’ll take care of it.' },
  declined: { color: 'gray', text: 'You declined. We’ll leave the item as it is.' },
  acknowledged: { color: 'blue', text: 'Thanks for letting us know you saw this.' },
  cancelled: { color: 'gray', text: 'The shop withdrew this request. Nothing to do.' },
};

export default function ItemFlagApproval() {
  const { flagId } = useParams();
  const [params] = useSearchParams();
  const laundryId = params.get('laundryId') || '';
  const t = params.get('t') || '';

  const [flag, setFlag] = useState(null);
  const [laundryName, setLaundryName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [photo, setPhoto] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ laundryId, t }).toString();
      const res = await fetch(`${API_BASE}/api/item-flags/public/${encodeURIComponent(flagId)}?${qs}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'This link is not valid.');
      setFlag(data.flag);
      setLaundryName(data.laundryName || '');
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [flagId, laundryId, t]);

  useEffect(() => { load(); }, [load]);

  const respond = async (approve) => {
    setSending(approve ? 'approve' : 'decline');
    try {
      const res = await fetch(`${API_BASE}/api/item-flags/public/${encodeURIComponent(flagId)}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ laundryId, t, approve }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not send your answer. Please try again.');
      setFlag(data.flag);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return (
      <VStack minH="60vh" justify="center"><Spinner size="lg" /></VStack>
    );
  }

  if (!flag) {
    return (
      <Box maxW="md" mx="auto" p={6}>
        <Alert status="warning" borderRadius="md"><AlertIcon />{error || 'This link is not valid.'}</Alert>
      </Box>
    );
  }

  const hasCharge = flag.chargeAmount != null;
  const answered = ANSWERED[flag.status];
  const photos = flag.photoUrls || [];

  return (
    <Box maxW="md" mx="auto" p={{ base: 4, md: 6 }}>
      <VStack align="stretch" spacing={4}>
        <Box>
          {laundryName && <Text fontSize="sm" color="gray.500">{laundryName}</Text>}
          <Heading size="md">
            {hasCharge ? `Approve ${flag.chargeLabel || 'treatment'}?` : `A note about your ${flag.itemLabel}`}
          </Heading>
          <Text fontSize="sm" color="gray.500">Order {flag.orderId}</Text>
        </Box>

        {photos.length > 0 && (
          <Box>
            <Image
              src={photos[photo]}
              alt={`Photo of your ${flag.itemLabel}`}
              borderRadius="lg"
              w="100%"
              maxH="420px"
              objectFit="contain"
              bg="gray.50"
            />
            {photos.length > 1 && (
              <SimpleGrid columns={photos.length} spacing={2} mt={2}>
                {photos.map((src, i) => (
                  <Image
                    key={src}
                    src={src}
                    alt={`Photo ${i + 1}`}
                    h="64px"
                    w="100%"
                    objectFit="cover"
                    borderRadius="md"
                    cursor="pointer"
                    borderWidth={2}
                    borderColor={i === photo ? 'blue.400' : 'transparent'}
                    onClick={() => setPhoto(i)}
                  />
                ))}
              </SimpleGrid>
            )}
          </Box>
        )}

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <HStack justify="space-between" mb={1}>
            <Text fontWeight="bold">{flag.itemLabel}</Text>
            <Badge colorScheme="orange">{flag.flagTypeLabel}</Badge>
          </HStack>
          <Text>We found {flag.issue}.</Text>
          {flag.note && <Text mt={2} color="gray.600">{flag.note}</Text>}
          {hasCharge && (
            <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px">
              <Text textTransform="capitalize">{flag.chargeLabel}</Text>
              <Text fontWeight="bold" fontSize="lg">{money(flag.chargeAmount)}</Text>
            </HStack>
          )}
        </Box>

        {error && <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>}

        {answered ? (
          <Alert status={flag.status === 'approved' ? 'success' : 'info'} borderRadius="md">
            <AlertIcon />{answered.text}
          </Alert>
        ) : hasCharge ? (
          <VStack align="stretch" spacing={2}>
            <Button
              colorScheme="blue"
              size="lg"
              onClick={() => respond(true)}
              isLoading={sending === 'approve'}
              isDisabled={!!sending}
            >
              Yes, add {money(flag.chargeAmount)}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => respond(false)}
              isLoading={sending === 'decline'}
              isDisabled={!!sending}
            >
              No thanks, leave it
            </Button>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              If you approve, {money(flag.chargeAmount)} is added to this order’s total.
              Some stains can’t be fully removed.
            </Text>
          </VStack>
        ) : (
          <Button colorScheme="blue" size="lg" onClick={() => respond(true)} isLoading={!!sending}>
            Got it
          </Button>
        )}
      </VStack>
    </Box>
  );
}
